/**
 * Strip HTML tags and decode basic HTML entities, returning plain text
 * suitable for use in `aria-label` and other AT-facing string attributes.
 *
 * Intentionally regex-based (not DOM-based) so it works in SSR and test
 * environments without a browser. Originally designed for pgroonga_snippet_html
 * output (`<span class="keyword">` wraps + htmlspecialchars-escaped source text).
 * Also used for full legislation HTML from the API (legislation-comparison feature),
 * which produces a wider tag set and includes `&nbsp;` / `&#160;` entities in
 * Slovak legal reference notation (e.g. `§&nbsp;1`, `ods.&nbsp;2`).
 *
 * Entity decoding is required because `aria-label` is set via JavaScript
 * property assignment (not innerHTML), so the browser does NOT decode entities.
 * Without decoding, `&amp;` would be announced as "ampersand amp semicolon"
 * by screen readers rather than "&" — a problem for Slovak legal text that
 * includes company names ("Smith & Partners s.r.o.") and legal comparators.
 *
 * Decoded entities:
 *   &amp;  → &    &lt; → <    &gt; → >    &quot; → "    &apos; → '
 *   &nbsp; → U+0020 (SPACE)    &#160; → U+0020 (SPACE)
 *
 * The non-breaking space entities (&nbsp; / &#160;) appear frequently in Slovak
 * legislation source text (e.g. `§&nbsp;1`, `ods.&nbsp;2`) and must be decoded
 * to a plain space so that word-splitting (e.g. in calculateStats) counts them
 * as separate words rather than a single run of characters. Without this decode,
 * `§&nbsp;1` would be treated as one word instead of two.
 *
 * Note: the hex variant `&#xA0;` (same U+00A0 codepoint) is NOT decoded — it is
 * not produced by pgroonga_snippet_html or the legislation API in practice.
 */
export function stripHtml(html: string): string {
  return html
    .replace(/<[^>]*>/g, '')
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/&apos;/g, "'")
    .replace(/&nbsp;/g, ' ')
    .replace(/&#160;/g, ' ');
}
